import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { clearApiCache } from "@/lib/api";

const AuthCtx = createContext(null);

const ACTIVE_KEY = "ndis_active_client_id";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = checking, null = not logged in
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [subscription, setSubscription] = useState(null); // {status, days_remaining, is_active, is_admin, trial_ends_at}
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [activeClientId, _setActiveClientId] = useState(() => localStorage.getItem(ACTIVE_KEY) || "");

  const refreshSubscription = useCallback(async () => {
    try { const { data } = await api.get("/me/subscription"); setSubscription(data); return data; }
    catch { setSubscription(null); return null; }
  }, []);

  const refreshUnreadMessages = useCallback(async () => {
    try { const { data } = await api.get("/messages/unread-count"); setUnreadMessages(data.count || 0); }
    catch { /* swallow */ }
  }, []);

  const setActiveClientId = useCallback(async (cid) => {
    if (cid) localStorage.setItem(ACTIVE_KEY, cid);
    else localStorage.removeItem(ACTIVE_KEY);
    _setActiveClientId(cid);
    // Switching clients invalidates all cached lists (they were scoped to the prior client).
    clearApiCache();
    // Re-fetch user so server returns the now-active engagement's worker_id/client_id.
    try { const { data } = await api.get("/auth/me"); setUser(data); } catch { /* ignore */ }
  }, []);

  const fetchClients = useCallback(async () => {
    try {
      const { data } = await api.get("/me/clients");
      setClients(data);
      // First-time: pick the first one as active so the header is always populated.
      if (!localStorage.getItem(ACTIVE_KEY) && data.length > 0) {
        const first = data.find((c) => c.active) || data[0];
        localStorage.setItem(ACTIVE_KEY, first.client_id);
        _setActiveClientId(first.client_id);
      }
      return data;
    } catch {
      setClients([]);
      return [];
    }
  }, []);

  const fetchMe = useCallback(async () => {
    const token = localStorage.getItem("ndis_hub_token");
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      await fetchClients();
      await refreshSubscription();
    } catch {
      localStorage.removeItem("ndis_hub_token");
      localStorage.removeItem(ACTIVE_KEY);
      setUser(null);
      setSubscription(null);
    } finally {
      setLoading(false);
    }
  }, [fetchClients, refreshSubscription]);

  useEffect(() => {
    // Skip the /me check if we're in the middle of a Google OAuth callback —
    // AuthCallback will fetch the profile and call refresh() itself.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("ndis_hub_token", data.token);
    localStorage.removeItem(ACTIVE_KEY); // reset active client on a fresh login
    _setActiveClientId("");
    setUser(data.user);
    await fetchClients();
    await refreshSubscription();
    return data.user;
  };

  const registerClient = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("ndis_hub_token", data.token);
    setUser(data.user);
    await fetchClients();
    await refreshSubscription();
    return data.user;
  };

  const registerWorker = async (payload) => {
    const { data } = await api.post("/auth/worker/register", payload);
    localStorage.setItem("ndis_hub_token", data.token);
    setUser(data.user);
    await fetchClients();
    await refreshSubscription();
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("ndis_hub_token");
    localStorage.removeItem(ACTIVE_KEY);
    _setActiveClientId("");
    setClients([]);
    setUser(null);
    setSubscription(null);
    clearApiCache();
  };

  return (
    <AuthCtx.Provider value={{
      user, loading, login, registerClient, registerWorker, logout,
      refresh: fetchMe,
      clients, activeClientId, setActiveClientId, refreshClients: fetchClients,
      subscription, refreshSubscription,
      unreadMessages, refreshUnreadMessages,
    }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
